// src/pages/Customer/CustomerViewPage.jsx
import React, { useState } from "react";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Table,
  Tag,
  Modal,
  Tooltip,
} from "antd";
import moment from "moment";
import { useParams } from "react-router";
import useApi from "../../hooks/useApi";
import useRequest from "../../hooks/useRequest";
import { customerStats, getKyus } from "../../constants/API/API";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";
import CancelImg from "../../assets/images/rejected-new-image.png";


const columns = [
  {
    title: "Booking ID",
    dataIndex: "bookingId",
    render: (_, record) => record?.bookingId || "-",
  },
  {
    title: "Parent Name",
    render: (_, record) =>
      record?.studentId?.name || record?.userId?.name || "-",
  },
  {
    title: "Class",
    render: (_, record) => record?.classId?.name || "-",
  },
  {
    title: "Branch",
    render: (_, record) => record?.branchId?.name || "-",
  },
  {
    title: "Trainer",
    render: (_, record) => record?.trainerId?.name || "-",
  },
  {
    title: "Recurring",
    render: (_, record) =>
      record?.recurring?.isRecurring ? (
        <Tag color="green">Yes</Tag>
      ) : (
        <Tag color="red">No</Tag>
      ),
  },
  {
    title: "Date",
    render: (_, record) =>
      record?.date ? moment(record.date).format("ll") : "-",
  },
  {
    title: "Week Day",
    render: (_, record) =>
      record?.date ? moment(record.date).format("dddd") : "-",
  },
  {
    title: "Time Slot",
    dataIndex: "slot",
  },
  {
    title: "Status",
    render: (_, record) => <Tag>{record?.status}</Tag>,
  },
  {
    title: "Created At",
    dataIndex: "createdAt",
    render: (d) => (d ? new Date(d).toLocaleDateString() : "-"),
  },
];

const examColumns = [
  { title: "Student", dataIndex: ["userId", "name"] },
  { title: "Belt", dataIndex: ["beltId", "name"] },
  { title: "Branch", dataIndex: ["branchId", "name"] },
  { title: "Conducted By", dataIndex: ["conductedBy", "name"] },
  {
    title: "Exam Date",
    dataIndex: "examDate",
    render: (d) => moment(d).format("ll"),
  },
  { title: "Score", dataIndex: "score" },
  { title: "Result", dataIndex: "result" },
];

const CustomerViewPage = () => {
  const { id } = useParams();
  const { request } = useRequest();

  const [student, setStudent] = useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelRecord, setCancelRecord] = useState(null);
  const [cancelAllRecurring, setCancelAllRecurring] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  /* ===========================
     Fetch Student Data
  =========================== */
  useApi({
    callback: customerStats,
    payload: id,
    dependencies: [id, refresh],
    shouldCallApi: true,
    onSuccess: (data) => setStudent(data),
    onError: (err) => ShowToast(err, Severty.ERROR),
  });

  const [kyuList, setKyuList] = useState([]);
  useApi({
    callback: getKyus,
    payload: {},
    dependencies: [],
    shouldCallApi: true,
    onSuccess: ({ data }) => {
      setKyuList(data?.docs || data || []);
    },
  });

  const handleCancelBooking = async () => {
    if (!cancelRecord) return;

    const userId = cancelRecord?.studentId || cancelRecord?.userId;
    if (!userId) {
      ShowToast("Student info missing!", Severty.ERROR);
      return;
    }

    const payloadUrl = `${apiPath.cancelBooking}/${cancelRecord._id}?userId=${userId}&cancelAll=${cancelAllRecurring}`;

    setLoading(true);

    request({
      url: payloadUrl,
      method: "POST",
      onSuccess: (res) => {
        ShowToast(
          res?.message || "Booking cancelled successfully!",
          Severty.SUCCESS
        );
        setRefresh((x) => !x);
        setIsCancelModalOpen(false);
        setCancelAllRecurring(false);
        setCancelRecord(null);
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(err?.message || "Something went wrong!", Severty.ERROR);
        setLoading(false);
      },
    });
  };

  const upcomingColumns = [
    ...columns,
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Tooltip title="Cancel Booking" color="purple">
          <Button
            type="primary"
            danger
            onClick={() => {
              setCancelRecord(record);
              setIsCancelModalOpen(true);
            }}
          >
            <img src={CancelImg} alt="Cancel" />
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Button className="mb-2" onClick={() => window.history.back()}>
        Back
      </Button>

      <Card title="Student Overview" className="mb-4">
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Name">
            {student?.user?.name || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Role">Student</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={student?.user?.isActive ? "green" : "red"}>
              {student?.user?.isActive ? "Active" : "Inactive"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Joined">
            {moment(student?.user?.createdAt).format("ll")}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Row gutter={16}>
        <Col span={12}>
          <Card title="Kyu Progress">
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Current Kyu">
                {student?.kyuProgress?.kyuId?.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Class Name">
                {student?.kyuProgress?.classId?.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Missed Sessions">
                {student?.attendance?.missedCount || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Attended Sessions">
                {student?.progress?.classProgress?.[0]?.attendanceCount || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Earned Kyus">
                {student?.kyuProgress?.grantedKyus?.length > 0 ? (
                  <div className="d-flex align-items-center gap-1 flex-wrap">
                    {student.kyuProgress.grantedKyus.map((k, idx) => {
                      const kyuObj = typeof k === "object" ? k : kyuList?.find?.((item) => (item._id || item.id) === k);
                      const kyuName = kyuObj?.name || (typeof k === "object" ? k?.name : null) || k;
                      return (
                        <Tag key={k?._id || idx} color="blue">
                          {kyuName}
                        </Tag>
                      );
                    })}
                  </div>
                ) : (
                  <span className="text-muted">No kyu earned</span>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Student Progress">
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Current Belt">
                {student?.progress?.beltId?.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Required Classes">
                {student?.progress?.requiredClasses || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Attendance Count">
                {student?.progress?.classProgress?.[0]?.attendanceCount || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Eligible For Graduation">
                {student?.progress?.eligibleForGraduation ? "Yes" : "No"}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      <div className="table-responsive customPagination">

        <Card title="Upcoming Sessions" className="mt-4">
          <Table
            columns={upcomingColumns}
            dataSource={student?.upcomingBookings || []}
            rowKey="_id"
            pagination={false}
            bordered
          />
        </Card>
      </div>

      <div className="table-responsive customPagination">
        <Card title="Completed Sessions" className="mt-4">
          <Table
            columns={columns}
            dataSource={student?.completedBookings || []}
            rowKey="_id"
            pagination={false}
            bordered
          />
        </Card>
      </div>
      <div className="table-responsive customPagination">
        <Card title="Previous Exams History" className="mt-4">
          <Table
            columns={examColumns}
            dataSource={student?.exams || []}
            rowKey="_id"
            pagination={false}
            bordered
          />
        </Card>
      </div>
      <Modal
        title="Cancel Booking"
        open={isCancelModalOpen}
        onCancel={() => {
          setIsCancelModalOpen(false);
          setCancelAllRecurring(false);
          setCancelRecord(null);
        }}
        onOk={handleCancelBooking}
        okText="Cancel Booking"
        okButtonProps={{ danger: true, loading }}
      >
        <p>Are you sure you want to cancel this booking?</p>

        {cancelRecord?.recurring?.isRecurring && (
          <div style={{ marginTop: 10 }}>
            <input
              type="checkbox"
              checked={cancelAllRecurring}
              onChange={(e) => setCancelAllRecurring(e.target.checked)}
              id="cancelAllRecurring"
            />
            <label htmlFor="cancelAllRecurring" style={{ marginLeft: 8 }}>
              Cancel all recurring bookings
            </label>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CustomerViewPage;
